import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  VStack,
  HStack,
  StackItem,
  Divider,
  Heading,
  Icon,
  IconButton,
  Text,
  Button,
  Markdown,
  Spinner,
  Banner,
  TextArea,
} from "@astryxdesign/core";
import { Theme } from "@astryxdesign/core/theme";
import { neutralTheme } from "@astryxdesign/theme-neutral";
import { MessageCircle, ArrowUp, Copy, Check, X } from "lucide-react";
import FloatingCorner from "./FloatingCorner";
import { useDocumentTheme } from "../hooks/useDocumentTheme";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";

export interface IChatMessage {
  role: "user" | "assistant";
  text: string;
}

export interface ChatPanelProps {
  messages: IChatMessage[];
  /** Called when the user submits a message. The parent is responsible for
   *  appending the user message optimistically and the assistant reply on
   *  resolution. */
  onSend: (message: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onDismissError?: () => void;
  /** Shown as quick-action buttons in the empty state. */
  starterPrompts?: string[];
  /** Panel heading and launcher button label. Defaults to "Ask AI". */
  title?: string;
  /** Supporting copy shown above the starter prompts in the empty state. */
  emptyStateText?: string;
}

const TEXTAREA_MAX_HEIGHT = 132; // ~5-6 rows before the field scrolls
const PANEL_HEIGHT = "min(600px, calc(100vh - 96px))";

/**
 * A floating, collapsible chat panel that renders a message thread, a
 * composing textarea, and an optional empty-state with starter prompts.
 *
 * All message state and network logic live in the parent — this component is
 * purely presentational. Wrap it in an Astryx `<LayerProvider>` if you need
 * tooltips or popovers inside the panel.
 */
const ChatPanel = ({
  messages,
  onSend,
  isLoading = false,
  error = null,
  onDismissError,
  starterPrompts = [],
  title = "Ask AI",
  emptyStateText,
}: ChatPanelProps): React.ReactElement => {
  const mode = useDocumentTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { copied, copy } = useCopyToClipboard();

  // When opening, scroll to the latest message and focus the input.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
        textareaRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Grow the textarea to fit its content, then let it scroll past the cap.
  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  };

  const handleSend = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || isLoading) return;

    setInput("");
    // Reset the textarea back to a single row after sending.
    requestAnimationFrame(() => {
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    });

    try {
      await onSend(message);
      setTimeout(scrollToBottom, 50);
    } catch {
      // Error display is the parent's responsibility via the error prop.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (text: string, index: number) => {
    setCopiedIndex(index);
    copy(text);
  };

  const isEmpty = messages.length === 0;

  return (
    <FloatingCorner>
      <Theme theme={neutralTheme} mode={mode}>
        {isOpen ? (
          <Card
            variant="default"
            elevation="high"
            padding={0}
            width={380}
            height={PANEL_HEIGHT}
          >
            <VStack height="100%" gap={0}>
              <HStack
                justify="between"
                align="center"
                padding={3}
                paddingInline={4}
              >
                <HStack gap={2} align="center">
                  <Icon icon={MessageCircle} color="accent" />
                  <Heading level={4}>{title}</Heading>
                </HStack>
                <IconButton
                  icon={<Icon icon={X} />}
                  label="Collapse chat"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                />
              </HStack>
              <Divider />

              <StackItem size="fill" isScrollable>
                <VStack gap={3} padding={4}>
                  {isEmpty && !isLoading && (
                    <VStack gap={3}>
                      {emptyStateText && (
                        <Text color="secondary">{emptyStateText}</Text>
                      )}
                      {starterPrompts.length > 0 && (
                        <VStack gap={2}>
                          {starterPrompts.map((prompt) => (
                            <Button
                              key={prompt}
                              label={prompt}
                              variant="ghost"
                              size="sm"
                              width="100%"
                              onClick={() => handleSend(prompt)}
                            />
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  )}

                  {messages.map((msg, i) => {
                    const isUser = msg.role === "user";
                    const isCopied = copied && copiedIndex === i;
                    return (
                      <HStack key={i} justify={isUser ? "end" : "start"}>
                        <Card
                          variant={isUser ? "blue" : "muted"}
                          padding={3}
                          maxWidth="85%"
                        >
                          <VStack gap={1}>
                            <Text type="supporting" color="secondary">
                              {isUser ? "You" : "AI"}
                            </Text>
                            {isUser ? (
                              <Text>{msg.text}</Text>
                            ) : (
                              <>
                                <Markdown density="compact">
                                  {msg.text}
                                </Markdown>
                                <HStack justify="end">
                                  <Button
                                    label={isCopied ? "Copied" : "Copy"}
                                    icon={
                                      <Icon icon={isCopied ? Check : Copy} />
                                    }
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopy(msg.text, i)}
                                  />
                                </HStack>
                              </>
                            )}
                          </VStack>
                        </Card>
                      </HStack>
                    );
                  })}

                  {isLoading && (
                    <HStack gap={2} align="center">
                      <Spinner size="sm" />
                      <Text type="supporting" color="secondary">
                        Thinking…
                      </Text>
                    </HStack>
                  )}
                  <div ref={messagesEndRef} />
                </VStack>
              </StackItem>

              {error && (
                <VStack paddingInline={4} paddingBlock={3}>
                  <Banner
                    status="error"
                    title={error}
                    isDismissable
                    onDismiss={onDismissError}
                  />
                </VStack>
              )}

              <Divider />
              <VStack gap={1} padding={3}>
                <HStack gap={2} align="end">
                  <StackItem size="fill">
                    <TextArea
                      ref={textareaRef}
                      label="Ask a question"
                      isLabelHidden
                      placeholder="Ask a question…"
                      value={input}
                      onChange={(value) => {
                        setInput(value);
                        resizeTextarea();
                      }}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      isDisabled={isLoading}
                    />
                  </StackItem>
                  <IconButton
                    icon={<Icon icon={ArrowUp} />}
                    label={isLoading ? "Thinking" : "Send question"}
                    variant="primary"
                    onClick={() => handleSend()}
                    isDisabled={isLoading || !input.trim()}
                  />
                </HStack>
                <Text type="supporting" color="secondary">
                  <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd>{" "}
                  for a new line
                </Text>
              </VStack>
            </VStack>
          </Card>
        ) : (
          <Button
            label={title}
            icon={<Icon icon={MessageCircle} />}
            variant="primary"
            elevation="high"
            onClick={() => setIsOpen(true)}
          />
        )}
      </Theme>
    </FloatingCorner>
  );
};

export default ChatPanel;
